import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import APIConstant from '../../utils/pathConstants';
import api from '../../utils/apiClient';
import Toast from '../Shared/Toast/Toast';
import Loader from '../Shared/Loader/Loader';
import AcademicYearSelector from '../AcademicYearSelector/AcademicYearSelector';
import UpdateBanner from '../UpdateBanner/UpdateBanner';
import useSocket from '../../utils/useSocket';
import useTokenExpiration from '../../utils/useTokenExpiration';
import { useCatchResponse } from '../../utils/useCatchResponse';

const Header = ({ showAcademicSelector = true, isExpanded }) => {
    const { catchResponse } = useCatchResponse();
    const navigate = useNavigate();
    const op = useRef(null);
    const childPanelRef = useRef(null);
    const [userData, setUserData] = useState({});
    const [profilePath, setProfilePath] = useState(undefined);
    const [newBuildFound, setNewBuildFound] = useState(false);
    const [isChangePassword, setIsPasswordChange] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [childreenList, setChildreenList] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState({});
    const [errors, setErrors] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [isSessionsOpen, setIsSessionsOpen] = useState(false);
    const [sessionList, setSessionList] = useState([]);

    useSocket(() => {
        if (CommonConfig.getCookie('loggedIn')) {
            setNewBuildFound(true);
        }
    });
    useTokenExpiration();

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        if (loginData?._id) {
            setSelectedAcademicYear(loginData?.SelectedAcademicYearList);
            fetchUserDetail(loginData._id);
            if (loginData?.UserRole === 'Parent') {
                GetChildreenList(loginData._id);
            }
            getUserAccessList(loginData._id, loginData.Token);

            const handleProfileUpdate = () => fetchUserDetail(loginData._id);
            window.addEventListener("userProfileUpdated", handleProfileUpdate);
            return () => window.removeEventListener("userProfileUpdated", handleProfileUpdate);
        }
    }, []);

    const GetChildreenList = async (id) => {
        try {
            let data = {
                UserID: id
            }
            await api.post(APIConstant.path.getChildreenDetailList, data).then(async response => {
                let res = response;
                if (res.success) {
                    setChildreenList(res.data);
                    let loginData = CommonConfig.loginData();
                    const childId = loginData?.SelectedChildData?._id ?? '';
                    const filterData = res?.data?.find(x => x?.id === childId);
                    if (loginData.SelectedChildData) {
                        loginData.SelectedChildData.AcademicDataList = filterData ? filterData?.AcademicDataList || [] : [];
                        localStorage.setItem('ERPLoginData', JSON.stringify(loginData));
                        window.dispatchEvent(new Event('local-storage-change'));
                    }
                    Loader.hide();
                }
            }).catch(err => {
                Loader.hide();
                console.log(err);
            });
        } catch (err) {
            console.log(err);
            catchResponse(err);
        }
    }

    const fetchUserDetail = async (id) => {
        try {
            const res = await api.post(APIConstant.path.getUserById, { id });
            if (res.success) {
                setUserData(res.data);
                setProfilePath(res.data.ProfilePhotoUrl ? `${apiBase}/UserProfilePic/${res.data.ProfilePhotoUrl}` : null);
            }
        } catch (err) {
            catchResponse(err);
        }
    };

    const getUserAccessList = async (userId, token) => {
        try {
            const res = await api.post(APIConstant.path.getUserWiseAccessList, { UserList: [userId] }, {
                header: { 'Authorization': `Bearer ${token}` }
            });
            if (res.success) {
                localStorage.setItem('userAccessData', JSON.stringify(res.data[0]?.ServiceList));
            }
        } catch (err) {
            console.error('Error fetching user access:', err);
        }
    };

    const handleLogout = async () => {
        try {
            Loader.show();
            const res = await CommonConfig.handleLogout(userData?._id, navigate);
            Loader.hide();
        } catch (err) {
            console.log('errrororor: ', err);

            Loader.hide();
            catchResponse(err);
        }
    };

    const formatLastActive = (d) => {
        if (!d) return '';
        const diff = Date.now() - new Date(d).getTime();
        const m = Math.floor(diff / 60000);
        if (m < 1) return 'Active just now';
        if (m < 60) return `Active ${m} min ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `Active ${h} hr ago`;
        const days = Math.floor(h / 24);
        return `Active ${days} day${days > 1 ? 's' : ''} ago`;
    };

    const loadMySessions = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.getMySessions, {});
            Loader.hide();
            if (res.success) {
                setSessionList(res.data || []);
            } else {
                Toast.error({ message: res.message || 'Failed to load sessions' });
            }
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const openActiveSessions = async () => {
        if (op.current) op.current.hide();
        setIsSessionsOpen(true);
        await loadMySessions();
    };

    const handleLogoutSession = async (session) => {
        if (session?.current) return;
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.logoutMySession, { sessionId: session._id });
            Loader.hide();
            if (res.success) {
                setSessionList((prev) => prev.filter((s) => s._id !== session._id));
                Toast.success({ message: res.message || 'Device logged out' });
            } else {
                Toast.error({ message: res.message || 'Failed to log out that device' });
            }
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const handlePasswordChange = (e, type) => {
        const value = e.target.value;
        setErrors(prev => ({ ...prev, [type]: '' }));
        switch (type) {
            case 'oldPassword':
                setOldPassword(value);
                break;
            case 'newPassword':
                setNewPassword(value);
                break;
            case 'confirmPassword':
                setConfirmPassword(value);
                break;
            default:
                break;
        }
    };

    const validatePassword = () => {
        let isValid = true;
        let newErrors = {};

        if (!newPassword) {
            newErrors.newPassword = 'Please enter new password';
            isValid = false;
        }
        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm password';
            isValid = false;
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Confirm password does not match';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const changePassword = async () => {
        if (!validatePassword()) return;

        try {
            const loginData = CommonConfig.loginData();
            const res = await api.post(APIConstant.path.userChangePassword, {
                id: loginData?._id,
                Password: newPassword,
            });
            if (res.success) {
                Toast.success({ message: res.message });
                CommonConfig.getUserMenu(loginData, navigate, 'Login');
            } else {
                Toast.error({ message: res.message });
            }
        } catch (err) {
            catchResponse(err);
        }
    };

    const renderChangePasswordFooter = () => (
        <div className="p-d-flex p-ai-center p-jc-end">
            <Button
                label="Cancel"
                className="btn-text-action-secondary"
                onClick={() => setIsPasswordChange(false)}
            />
            <Button
                label="Save"
                className="btn-text-action-primary"
                onClick={changePassword}
            />
        </div>
    );

    const updateBuildConfiguration = async () => {
        try {
            const loginData = CommonConfig.loginData();
            await api.post(APIConstant.path.UpdateBuildConfigurationById, { UserID: loginData?._id });
            setNewBuildFound(false);
            window.location.reload();
        } catch (error) {
            catchResponse(error);
        }
    };

    const handleCardClick = async (id, name, userType) => {
        const loginData = CommonConfig.loginData();
        const selectedChild = childreenList.find(child => child?.id === id);
        if (!loginData.SelectedChildData) {
            loginData.SelectedChildData = {};
        }
        loginData.SelectedChildData._id = id;
        loginData.SelectedChildData.Name = name;
        loginData.SelectedChildData.UserType = userType;
        loginData.SelectedChildData.ProfilePhotoUrl = selectedChild?.ProfilePhotoUrl || '';
        loginData.SelectedChildData.AcademicDataList = selectedChild?.AcademicDataList || [];
        localStorage.setItem('ERPLoginData', JSON.stringify(loginData));
        await CommonConfig.getUserMenu(loginData, navigate, 'OnClick');
        setTimeout(() =>
            navigate(0), 500)
    }

    return (
        <>
            <div className="user cursor-pointer" onClick={(e) => op.current.toggle(e)}>
                <div className="user-left" >
                    {newBuildFound && <UpdateBanner onManualRefresh={updateBuildConfiguration} />}
                    <div className={isExpanded ? "user-avatar" : "user-avatar p-mr-0"}>
                        <img src={profilePath} alt="" />
                    </div>
                    {isExpanded ? <div className="user-info">
                        {userData?.Name}
                        {(userData?.UserType !== 'Parent' && showAcademicSelector) ? (
                            <div className="et-ay-inline" onClick={(e) => e.stopPropagation()}>
                                <i className="pi pi-calendar" />
                                <AcademicYearSelector compact />
                            </div>
                        ) : null}
                        <small>{userData?.UserType}</small>
                    </div> : null}
                </div>
                <div className="sidebar-head p-d-flex p-ai-center p-jc-end" style={{ left: '30px', top: "20px"}}>
                    {userData?.UserType === 'Parent' && isExpanded && (
                        <Button
                            type="button"
                            icon="pi pi-users"
                            className=""
                            onClick={(e) => {
                                e.stopPropagation();
                                if (childPanelRef && childPanelRef.current) childPanelRef.current.toggle(e);
                            }}
                        />
                    )}
                </div>
            </div>
            <OverlayPanel ref={op} className='profile-menu profile-menu-sidebar' appendTo={document.body}>
                <ul className='menu-options'>
                    {(userData?.UserType !== 'Parent' && showAcademicSelector) && <AcademicYearSelector />}
                    <li className='menu-link' onClick={() => navigate('/edit-profile')}>
                        <i className='icon-edit-profile'></i>
                        <span>Edit Profile</span>
                    </li>
                    <li className='menu-link' onClick={() => setIsPasswordChange(true)}>
                        <i className='icon-change-password'></i>
                        <span>Change Password</span>
                    </li>
                    <li className='menu-link' onClick={openActiveSessions}>
                        <i className='pi pi-desktop'></i>
                        <span>Active Sessions</span>
                    </li>
                    <li className='menu-link' onClick={handleLogout}>
                        <i className='icon-logout'></i>
                        <span>Logout</span>
                    </li>
                </ul>
            </OverlayPanel>
            <OverlayPanel
                ref={childPanelRef}
                showCloseIcon
                dismissable
                appendTo={document.body}
                style={{ width: '320px' }}
                className="child-overlay-panel"
            >
                <div style={{
                    padding: '0.5rem 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                }}>
                    <div style={{
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #e5e7eb',
                        marginBottom: '0.25rem'
                    }}>
                        <h4 style={{
                            margin: 0,
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            color: '#1c515e'
                        }}>
                            Select Student
                        </h4>
                    </div>

                    {childreenList?.length ? (
                        childreenList.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    handleCardClick(item?.id, item?.Name, item?.UserType);
                                    childPanelRef?.current?.hide();
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    padding: '0.875rem 1rem',
                                    margin: '0 0.5rem',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    border: '1px solid #e5e7eb',
                                    background: '#fff'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#f0fdf4';
                                    e.currentTarget.style.borderColor = '#14452f';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(20, 69, 47, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#fff';
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{
                                    position: 'relative',
                                    minWidth: '44px',
                                    maxWidth: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    background: '#ecfdf5',
                                    border: '2px solid #14452f',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {item?.ProfilePhotoUrl ? (
                                        <img
                                            src={`${apiBase}/UserProfilePic/${item.ProfilePhotoUrl}`}
                                            alt={item?.Name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <span style={{
                                            fontSize: '1rem',
                                            fontWeight: '700',
                                            color: '#14452f'
                                        }}>
                                            {item?.Name?.charAt(0)?.toUpperCase()}
                                        </span>
                                    )}
                                </div>

                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        color: '#1c515e',
                                        lineHeight: '1.4'
                                    }}>
                                        {item?.Name}
                                    </div>
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: '#64748b',
                                        marginTop: '0.2rem'
                                    }}>
                                        {item?.RollNo ? `Roll No: ${item.RollNo}` : 'No Roll Number'}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{
                            padding: '2rem 1rem',
                            textAlign: 'center',
                            color: '#64748b',
                            fontSize: '0.9rem'
                        }}>
                            No students found
                        </div>
                    )}
                </div>
            </OverlayPanel>
            <Dialog
                header={
                    <div className="p-d-flex p-jc-between p-ai-center">
                        <span>Change Password</span>
                    </div>
                }
                className='dialog-popup'
                visible={isChangePassword}
                onHide={() => setIsPasswordChange(false)}
                draggable={false}
                dismissableMask
                position="center"
                style={{ width: '450px' }}
                footer={renderChangePasswordFooter()}
            >
                <div className='p-fluid'>
                    <div className='p-field'>
                        <label htmlFor="oldPassword">Old Password</label>
                        <InputText id="oldPassword" type="password" value={oldPassword} onChange={(e) => handlePasswordChange(e, 'oldPassword')} />
                        {errors.oldPassword && <small className='p-error'>{errors.oldPassword}</small>}
                    </div>
                    <div className='p-field'>
                        <label htmlFor="newPassword">New Password</label>
                        <InputText id="newPassword" type="password" value={newPassword} onChange={(e) => handlePasswordChange(e, 'newPassword')} />
                        {errors.newPassword && <small className='p-error'>{errors.newPassword}</small>}
                    </div>
                    <div className='p-field'>
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <InputText id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => handlePasswordChange(e, 'confirmPassword')} />
                        {errors.confirmPassword && <small className='p-error'>{errors.confirmPassword}</small>}
                    </div>
                </div>
            </Dialog>

            <Dialog
                header={
                    <div className="p-d-flex p-jc-between p-ai-center">
                        <span>Active Sessions</span>
                    </div>
                }
                className='dialog-popup'
                visible={isSessionsOpen}
                onHide={() => setIsSessionsOpen(false)}
                draggable={false}
                dismissableMask
                position="center"
                style={{ width: '480px' }}
            >
                <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#6B7794' }}>
                    You can be signed in on up to 3 devices. Log out of a device to free a slot.
                </p>
                {sessionList.length === 0 ? (
                    <div style={{ padding: '16px 0', textAlign: 'center', color: '#6B7794' }}>No active sessions.</div>
                ) : (
                    <ul className='active-sessions-list' style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {sessionList.map((s) => (
                            <li
                                key={s._id}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '12px',
                                    padding: '12px 14px', marginBottom: '10px',
                                    background: '#F4F6FA', borderRadius: '10px',
                                }}
                            >
                                <i className='pi pi-desktop' style={{ fontSize: '20px', color: '#1c515e' }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#1c2b3a' }}>
                                        {s.device || 'Unknown device'}
                                        {s.current && (
                                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#1c515e' }}>
                                                (This device)
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6B7794' }}>{formatLastActive(s.lastActive)}</div>
                                </div>
                                {s.current ? (
                                    <span style={{
                                        fontSize: 11, fontWeight: 600, color: '#137a3f',
                                        background: '#e6f5ec', border: '1px solid #b7e3c6',
                                        borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap',
                                    }}>
                                        This device
                                    </span>
                                ) : (
                                    <Button
                                        label='Log out'
                                        className='btn-text-action-secondary'
                                        onClick={() => handleLogoutSession(s)}
                                    />
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </Dialog>
        </>
    );
};

export default Header;
